import React from "react";
import { filter, first, flatMap, map, meanBy, round, toNumber } from "lodash";
import Markdown from "react-markdown";
import { Note } from "@/features/workspace/graphql/generated";
import { Column, Row } from "@/shared/ui/Blocks";
import { Title } from "@/shared/ui/Title";
import { Card } from "@/shared/ui/Card";
import { EvaluationScoreRow } from "@/shared/ui/EvaluationScoreRow";
import PersonEvaluationsHoverOver from "@/shared/widgets/PersonEvaluationsHoverOver";
import { AssessmentEvaluation } from "@/features/people/graphql/generated";
import { cleanupMarkdownFromTags } from "@/shared/utils/parsers/cleanupMarkdownFromTags";

interface AssessmentRow {
  id: number;
  title: string;
  description?: string;
  summary?: string;
  evaluations: AssessmentEvaluation[]; // TODO: Add proper type for evaluations
  meta?: {
    [key: string]: string;
  };
  note?: Note;
}

interface AssessmentReportRowGroupProps {
  title: string;
  rows: AssessmentRow[];
  isSingleScoreAssessment?: boolean;
}

const getSpanScoreEvaluationAverages = (
  evaluations: AssessmentEvaluation[]
) => {
  const spanScoreEvaluations = filter(
    evaluations,
    (e) => e.meta?.type === "leadership_span"
  );

  if (spanScoreEvaluations.length === 0) {
    return undefined;
  }

  const left = round(meanBy(spanScoreEvaluations, (e) => e.meta?.score_left));
  const right = round(meanBy(spanScoreEvaluations, (e) => e.meta?.score_right));

  return [left, right] as [number, number];
};

export function AssessmentReportRowGroup({
  title,
  rows,
  isSingleScoreAssessment,
}: AssessmentReportRowGroupProps) {
  return (
    <Column className="w-full mb-4 transition-transform">
      <Row className="p-4 my-8 items-center bg-blue-100 print:bg-grey-100 w-full justify-between">
        <Column>
          <Title className="text-blue-500" as="h5">
            {title}
          </Title>
        </Column>
        <Row className="print:hidden">
          <EvaluationScoreRow
            score={round(
              meanBy(
                flatMap(rows, (r) => r.evaluations),
                "evaluation"
              )
            )}
            variant="numeric"
            colorScheme="blue"
          />
        </Row>
      </Row>
      {rows.map((r) => (
        <Column key={r.id} className="mb-12 break-inside-avoid">
          <Row className="items-center justify-between pr-4 break-inside-avoid print:pl-4">
            <Column>
              <Title as="h6" className="font-semibold">
                {r.title}
              </Title>
            </Column>
            {isSingleScoreAssessment && (
              <EvaluationScoreRow
                score={toNumber(first(r.evaluations)?.evaluation)}
                variant="numeric"
                colorScheme="blue-light"
              />
            )}
            {!isSingleScoreAssessment && (
              <PersonEvaluationsHoverOver
                evaluations={r.evaluations as any}
                title={r.title || ""}
                variant="numeric"
                colorScheme="blue-light"
              />
            )}
          </Row>
          {/* <SubtitleText
            className="mt-4"
            dangerouslySetInnerHTML={{
              __html: r.description || "",
            }}
          /> */}
          {r?.meta?.type === "leadership_span" &&
            !!getSpanScoreEvaluationAverages(r.evaluations) &&
            map(
              getSpanScoreEvaluationAverages(r.evaluations),
              (score, index) => (
                <Row
                  className="mt-4 items-center justify-between pr-4"
                  key={index}
                >
                  <Title as="h6" className=" text-grey-700">
                    {index === 0 ? r?.meta?.left : r?.meta?.right}
                  </Title>
                  <EvaluationScoreRow
                    score={score}
                    variant="numeric"
                    colorScheme="blue-light"
                  />
                </Row>
              )
            )}
          {!!r.note?.text?.length && (
            <Card className="p-4 bg-grey-100 w-full border-none rounded-xl shadow-none mt-4 prose prose-p:text-sm prose-p:text-grey-700 max-w-none prose-li:text-sm prose-li:text-grey-700">
              <Markdown>{cleanupMarkdownFromTags(r.note?.text || "")}</Markdown>
            </Card>
          )}
        </Column>
      ))}
    </Column>
  );
}

export default AssessmentReportRowGroup;
